import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from './config';

/**
 * VariableInspector — live side panel showing all node outputs and env variables
 * for the current run, with one-click "Copy as template reference" buttons.
 */
export default function VariableInspector({ nodes, nodeStatus, onClose }) {
  const [envVars, setEnvVars] = useState([]);
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}/global-variables`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => setEnvVars(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const copyToClipboard = useCallback((text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    });
  }, []);

  // Flatten an object into dot-path entries for display
  const flattenObject = (obj, prefix = '') => {
    if (obj === null || obj === undefined) return [{ path: prefix || '(null)', value: String(obj) }];
    if (typeof obj !== 'object' || Array.isArray(obj)) {
      return [{ path: prefix || '(value)', value: JSON.stringify(obj) }];
    }
    const entries = [];
    for (const [k, v] of Object.entries(obj)) {
      const path = prefix ? `${prefix}.${k}` : k;
      if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
        entries.push(...flattenObject(v, path));
      } else {
        entries.push({ path, value: JSON.stringify(v) });
      }
    }
    return entries.length ? entries : [{ path: prefix, value: '{}' }];
  };

  // Nodes that have status data
  const activeNodes = nodes.filter(n => nodeStatus[n.id]);

  const searchLower = search.toLowerCase();

  const matchesSearch = (label, entries) => {
    if (!searchLower) return true;
    if (label.toLowerCase().includes(searchLower)) return true;
    return entries.some(e => e.path.toLowerCase().includes(searchLower) || e.value.toLowerCase().includes(searchLower));
  };

  const statusColor = (status) => {
    if (status === 'completed') return 'var(--success-color, #4caf50)';
    if (status === 'running') return 'var(--info-color, #2196f3)';
    if (status === 'failed') return 'var(--error-color, #f44336)';
    if (status === 'waiting') return 'var(--warning-color, #ff9800)';
    return 'var(--text-tertiary)';
  };

  const copyBtn = (text, uniqueKey) => (
    <button
      onClick={() => copyToClipboard(text, uniqueKey)}
      title={`Copy: ${text}`}
      style={{
        marginLeft: '6px',
        padding: '1px 6px',
        fontSize: '10px',
        background: copiedKey === uniqueKey ? 'var(--success-color, #4caf50)' : 'var(--bg-hover, rgba(255,255,255,0.08))',
        color: copiedKey === uniqueKey ? '#fff' : 'var(--text-secondary)',
        border: '1px solid var(--border-color)',
        borderRadius: '3px',
        cursor: 'pointer',
        fontFamily: 'monospace',
        flexShrink: 0,
        whiteSpace: 'nowrap',
      }}
    >
      {copiedKey === uniqueKey ? '✓' : 'copy'}
    </button>
  );

  const toggleSection = (key) => setCollapsed(c => ({ ...c, [key]: !c[key] }));

  const renderNodeSection = (node) => {
    const ns = nodeStatus[node.id];
    const label = node.data?.label || node.id;
    const entries = ns.output !== null && ns.output !== undefined
      ? flattenObject(ns.output)
      : [];

    const filtered = searchLower
      ? entries.filter(e => e.path.toLowerCase().includes(searchLower) || e.value.toLowerCase().includes(searchLower) || label.toLowerCase().includes(searchLower))
      : entries;

    if (searchLower && !matchesSearch(label, entries)) return null;

    const sectionKey = `node-${node.id}`;
    const isOpen = !collapsed[sectionKey];

    return (
      <div key={node.id} style={{ marginBottom: '8px', border: '1px solid var(--border-color)', borderRadius: '6px', overflow: 'hidden' }}>
        {/* Section header */}
        <div
          onClick={() => toggleSection(sectionKey)}
          style={{
            padding: '8px 10px',
            background: 'var(--bg-node, rgba(255,255,255,0.04))',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            userSelect: 'none',
          }}
        >
          <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', flexShrink: 0 }}>{isOpen ? '▼' : '▶'}</span>
          <span style={{ flex: 1, fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
          <span style={{ fontSize: '10px', color: statusColor(ns.status), fontWeight: 600, flexShrink: 0 }}>{ns.status}</span>
        </div>

        {isOpen && (
          <div style={{ padding: '8px 10px', fontSize: '11px', fontFamily: 'monospace' }}>
            {/* Copy full output reference */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid var(--border-color)' }}>
              <span style={{ color: 'var(--text-tertiary)', fontSize: '10px', flex: 1 }}>Full output</span>
              {copyBtn(`{{ nodes['${label}'] }}`, `full-${node.id}`)}
            </div>

            {ns.status === 'failed' ? (
              <div style={{ color: 'var(--error-color, #f44336)', fontSize: '11px', wordBreak: 'break-all' }}>
                {String(ns.error || ns.output || 'Error')}
              </div>
            ) : filtered.length === 0 ? (
              <div style={{ color: 'var(--text-tertiary)', fontStyle: 'italic', fontSize: '11px' }}>
                {ns.status === 'running' ? 'Running…' : 'No output'}
              </div>
            ) : (
              filtered.map(({ path, value }) => {
                const ref = `{{ nodes['${label}'].${path} }}`;
                const uniqueKey = `${node.id}-${path}`;
                return (
                  <div key={path} style={{ display: 'flex', alignItems: 'flex-start', gap: '4px', marginBottom: '4px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ color: 'var(--accent-color, #7b68ee)' }}>{path}</span>
                      <span style={{ color: 'var(--text-tertiary)', margin: '0 4px' }}>=</span>
                      <span style={{ color: 'var(--text-secondary)', wordBreak: 'break-all' }}>{value.length > 80 ? value.slice(0, 80) + '…' : value}</span>
                    </div>
                    {copyBtn(ref, uniqueKey)}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    );
  };

  const filteredEnv = envVars.filter(v =>
    !searchLower || v.key?.toLowerCase().includes(searchLower) || v.key?.toLowerCase().includes(searchLower)
  );
  const envSectionKey = 'env';
  const envOpen = !collapsed[envSectionKey];

  return (
    <div style={{
      width: '300px',
      background: 'var(--bg-secondary)',
      borderLeft: '1px solid var(--border-color)',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      boxSizing: 'border-box',
      flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid var(--border-color)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: 'var(--bg-secondary)',
        flexShrink: 0,
      }}>
        <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>Variable Inspector</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '16px', padding: '2px 6px', lineHeight: 1 }} title="Close">✕</button>
      </div>

      {/* Search */}
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filter variables…"
          style={{
            width: '100%',
            padding: '6px 8px',
            background: 'var(--bg-input)',
            border: '1px solid var(--border-color)',
            borderRadius: '4px',
            color: 'var(--text-primary)',
            fontFamily: 'inherit',
            fontSize: '12px',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px' }}>

        {/* Node outputs */}
        <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
          Node Outputs
        </div>

        {activeNodes.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontStyle: 'italic', marginBottom: '20px' }}>
            Run the workflow to see outputs here.
          </div>
        ) : (
          activeNodes.map(renderNodeSection)
        )}

        {/* Env / Global Variables */}
        <div style={{ marginTop: '16px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
            Global Variables (env)
          </div>

          <div style={{ border: '1px solid var(--border-color)', borderRadius: '6px', overflow: 'hidden' }}>
            <div
              onClick={() => toggleSection(envSectionKey)}
              style={{ padding: '8px 10px', background: 'var(--bg-node, rgba(255,255,255,0.04))', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', userSelect: 'none' }}
            >
              <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>{envOpen ? '▼' : '▶'}</span>
              <span style={{ flex: 1, fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>env</span>
              <span style={{ fontSize: '10px', color: 'var(--text-tertiary)' }}>{filteredEnv.length} var{filteredEnv.length !== 1 ? 's' : ''}</span>
            </div>

            {envOpen && (
              <div style={{ padding: '8px 10px', fontSize: '11px', fontFamily: 'monospace' }}>
                {filteredEnv.length === 0 ? (
                  <div style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>No global variables defined.</div>
                ) : (
                  filteredEnv.map(v => {
                    const ref = `{{ env['${v.key}'] }}`;
                    const uniqueKey = `env-${v.key}`;
                    return (
                      <div key={v.key} style={{ display: 'flex', alignItems: 'flex-start', gap: '4px', marginBottom: '4px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ color: 'var(--accent-color, #7b68ee)' }}>{v.key}</span>
                          {!v.is_encrypted && (
                            <>
                              <span style={{ color: 'var(--text-tertiary)', margin: '0 4px' }}>=</span>
                              <span style={{ color: 'var(--text-secondary)' }}>{String(v.value).length > 40 ? String(v.value).slice(0, 40) + '…' : v.value}</span>
                            </>
                          )}
                          {v.is_encrypted && <span style={{ color: 'var(--text-tertiary)', marginLeft: '6px', fontSize: '10px' }}>🔒 encrypted</span>}
                        </div>
                        {copyBtn(ref, uniqueKey)}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Footer hint */}
      <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border-color)', fontSize: '10px', color: 'var(--text-tertiary)', flexShrink: 0 }}>
        Click <strong>copy</strong> to copy a template reference, e.g.{' '}
        <code style={{ fontSize: '9px', background: 'var(--bg-input)', padding: '1px 3px', borderRadius: '2px' }}>{'{{ nodes[\'My Node\'].email }}'}</code>
      </div>
    </div>
  );
}
